import type { ReactNode } from "react";

export type IconName =
	| "home"
	| "bell"
	| "chart"
	| "cart"
	| "box"
	| "tag"
	| "users"
	| "truck"
	| "factory"
	| "chat"
	| "card"
	| "bank"
	| "layout"
	| "shield"
	| "search"
	| "chevR"
	| "chevD"
	| "chevL"
	| "plus"
	| "filter"
	| "dots"
	| "upDown"
	| "check"
	| "alert"
	| "info"
	| "download"
	| "eye"
	| "image"
	| "copy"
	| "edit"
	| "print"
	| "refresh"
	| "flag"
	| "calendar"
	| "clock"
	| "star"
	| "map"
	| "pin"
	| "key"
	| "user"
	| "arrow"
	| "arrowUR"
	| "trend"
	| "close"
	| "mail"
	| "lock"
	| "eyeOff"
	| "phone"
	| "qr"
	| "google"
	| "trash"
	| "gear"
	| "logout"
	| "zap"
	| "percent";

interface AdminIconProps {
	n: IconName;
	s?: number;
	c?: string;
}

export function AdminIcon({ n, s = 16, c = "currentColor" }: AdminIconProps) {
	const paths: Record<IconName, ReactNode> = {
		zap: (
			<path
				d="M13 2L3 14h7l-1 8 10-12h-7l1-8Z"
				stroke={c}
				strokeWidth="1.6"
				fill="none"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		),
		percent: (
			<g stroke={c} strokeWidth="1.6" fill="none" strokeLinecap="round">
				<line x1="19" y1="5" x2="5" y2="19" />
				<circle cx="6.5" cy="6.5" r="2.5" />
				<circle cx="17.5" cy="17.5" r="2.5" />
			</g>
		),
		home: (
			<path
				d="M3 11 12 4l9 7v9h-6v-6h-6v6H3v-9Z"
				stroke={c}
				strokeWidth="1.6"
				fill="none"
				strokeLinejoin="round"
			/>
		),
		bell: (
			<path
				d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4l2-2Zm4 3a2 2 0 0 0 4 0"
				stroke={c}
				strokeWidth="1.6"
				fill="none"
				strokeLinejoin="round"
			/>
		),
		chart: (
			<g
				stroke={c}
				strokeWidth="1.6"
				fill="none"
				strokeLinecap="round"
				strokeLinejoin="round"
			>
				<path d="M4 19h16M6 16V8M11 16v-6M16 16v-3" />
			</g>
		),
		cart: (
			<path
				d="M3 4h2.5l1.4 11.5a2 2 0 0 0 2 1.7h7.6a2 2 0 0 0 2-1.5L20.5 8H7M9 21a1.2 1.2 0 1 0 0-2.5 1.2 1.2 0 0 0 0 2.5Zm9 0a1.2 1.2 0 1 0 0-2.5 1.2 1.2 0 0 0 0 2.5Z"
				stroke={c}
				strokeWidth="1.5"
				fill="none"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		),
		box: (
			<g stroke={c} strokeWidth="1.6" fill="none" strokeLinejoin="round">
				<path d="M3 7 12 3l9 4-9 4-9-4Z" />
				<path d="M3 7v10l9 4 9-4V7M12 11v10" />
			</g>
		),
		tag: (
			<g stroke={c} strokeWidth="1.6" fill="none">
				<path d="M3 12V5a2 2 0 0 1 2-2h7l9 9-9 9-9-9Z" strokeLinejoin="round" />
				<circle cx="8" cy="8" r="1.5" />
			</g>
		),
		users: (
			<g stroke={c} strokeWidth="1.6" fill="none">
				<circle cx="9" cy="8" r="3" />
				<path d="M3 19c0-3 2.7-5 6-5s6 2 6 5M16 5a3 3 0 1 1 0 6M14 19c.7-3 3.2-5 5-5" />
			</g>
		),
		truck: (
			<g
				stroke={c}
				strokeWidth="1.6"
				fill="none"
				strokeLinecap="round"
				strokeLinejoin="round"
			>
				<path d="M3 6h11v10H3zM14 9h4l3 3v4h-7" />
				<circle cx="7" cy="18" r="1.6" />
				<circle cx="17" cy="18" r="1.6" />
			</g>
		),
		factory: (
			<g stroke={c} strokeWidth="1.6" fill="none" strokeLinejoin="round">
				<path d="M3 21h18V11l-6 4V11l-6 4V11l-6 4Z" />
				<path d="M5 21V8h3v3" strokeLinecap="round" />
			</g>
		),
		chat: (
			<path
				d="M4 5h16v11H8l-4 4V5Z"
				stroke={c}
				strokeWidth="1.6"
				fill="none"
				strokeLinejoin="round"
			/>
		),
		card: (
			<g stroke={c} strokeWidth="1.6" fill="none" strokeLinecap="round">
				<rect x="3" y="6" width="18" height="12" rx="2" />
				<path d="M3 10h18M7 15h3" />
			</g>
		),
		bank: (
			<g stroke={c} strokeWidth="1.6" fill="none" strokeLinejoin="round">
				<path d="M3 10 12 4l9 6H3Z" />
				<path d="M5 10v8M9 10v8M15 10v8M19 10v8M3 21h18" />
			</g>
		),
		layout: (
			<g stroke={c} strokeWidth="1.6" fill="none">
				<rect x="3" y="4" width="18" height="16" rx="2" />
				<path d="M3 9h18M9 9v11" />
			</g>
		),
		shield: (
			<path
				d="M12 3 4 6v6c0 4.5 3.2 8 8 9 4.8-1 8-4.5 8-9V6l-8-3Zm-3 9 2.5 2.5L16 10"
				stroke={c}
				strokeWidth="1.5"
				fill="none"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		),
		search: (
			<path
				d="M11 4a7 7 0 1 1 0 14 7 7 0 0 1 0-14Zm0 2a5 5 0 1 0 0 10 5 5 0 0 0 0-10ZM20 20l-3.5-3.5"
				stroke={c}
				strokeWidth="1.6"
				fill="none"
				strokeLinecap="round"
			/>
		),
		chevR: (
			<path
				d="m9 6 6 6-6 6"
				stroke={c}
				strokeWidth="1.7"
				fill="none"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		),
		chevD: (
			<path
				d="m6 9 6 6 6-6"
				stroke={c}
				strokeWidth="1.7"
				fill="none"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		),
		chevL: (
			<path
				d="m15 6-6 6 6 6"
				stroke={c}
				strokeWidth="1.7"
				fill="none"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		),
		plus: (
			<path
				d="M12 5v14M5 12h14"
				stroke={c}
				strokeWidth="1.8"
				strokeLinecap="round"
			/>
		),
		filter: (
			<path
				d="M3 5h18M6 12h12M10 19h4"
				stroke={c}
				strokeWidth="1.8"
				strokeLinecap="round"
			/>
		),
		dots: (
			<g fill={c}>
				<circle cx="5" cy="12" r="1.6" />
				<circle cx="12" cy="12" r="1.6" />
				<circle cx="19" cy="12" r="1.6" />
			</g>
		),
		upDown: (
			<path
				d="m8 6-3 3M8 6v12M16 18l3-3M16 18V6"
				stroke={c}
				strokeWidth="1.6"
				fill="none"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		),
		check: (
			<path
				d="M5 12.5 10 17l9-10"
				stroke={c}
				strokeWidth="2.2"
				strokeLinecap="round"
				strokeLinejoin="round"
				fill="none"
			/>
		),
		alert: (
			<g
				stroke={c}
				strokeWidth="1.6"
				fill="none"
				strokeLinejoin="round"
				strokeLinecap="round"
			>
				<path d="M12 3 2 21h20L12 3Z" />
				<path d="M12 10v5M12 18v.5" />
			</g>
		),
		info: (
			<g stroke={c} strokeWidth="1.6" fill="none">
				<circle cx="12" cy="12" r="9" />
				<path d="M12 8v.5M12 11v5" strokeLinecap="round" />
			</g>
		),
		download: (
			<g
				stroke={c}
				strokeWidth="1.6"
				fill="none"
				strokeLinecap="round"
				strokeLinejoin="round"
			>
				<path d="M12 4v11M7 11l5 5 5-5M5 20h14" />
			</g>
		),
		image: (
			<g stroke={c} strokeWidth="1.6" fill="none">
				<rect x="3" y="4" width="18" height="16" rx="2" />
				<circle cx="8.5" cy="9.5" r="1.6" />
				<path d="m4 17 4.5-4.5a2 2 0 0 1 2.8 0L15 16" />
				<path d="m14 15 2-2a2 2 0 0 1 2.8 0L21 15.2" />
			</g>
		),
		copy: (
			<g stroke={c} strokeWidth="1.6" fill="none">
				<rect x="9" y="9" width="11" height="11" rx="2" />
				<path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
			</g>
		),
		eye: (
			<g stroke={c} strokeWidth="1.6" fill="none">
				<path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z" />
				<circle cx="12" cy="12" r="3" />
			</g>
		),
		edit: (
			<path
				d="m4 20 4-1 11-11-3-3L5 16l-1 4Z"
				stroke={c}
				strokeWidth="1.6"
				fill="none"
				strokeLinejoin="round"
			/>
		),
		print: (
			<g stroke={c} strokeWidth="1.6" fill="none" strokeLinejoin="round">
				<path d="M7 9V3h10v6M7 18H4v-9h16v9h-3M7 14h10v7H7z" />
			</g>
		),
		refresh: (
			<path
				d="M4 12a8 8 0 0 1 14-5.3M20 12a8 8 0 0 1-14 5.3M18 3v4h-4M6 21v-4h4"
				stroke={c}
				strokeWidth="1.6"
				fill="none"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		),
		flag: (
			<path
				d="M5 3v18M5 4h13l-2 4 2 4H5"
				stroke={c}
				strokeWidth="1.6"
				fill="none"
				strokeLinejoin="round"
			/>
		),
		calendar: (
			<g stroke={c} strokeWidth="1.6" fill="none">
				<rect x="3" y="5" width="18" height="16" rx="2" />
				<path d="M8 3v4M16 3v4M3 10h18" />
			</g>
		),
		clock: (
			<g stroke={c} strokeWidth="1.6" fill="none">
				<circle cx="12" cy="12" r="9" />
				<path d="M12 7v5l3 2" strokeLinecap="round" />
			</g>
		),
		star: (
			<path
				d="m12 3 2.6 5.3 5.9.9-4.2 4.1 1 5.8L12 16.5l-5.3 2.8 1-5.8L3.5 9.2l5.9-.9L12 3Z"
				fill={c}
			/>
		),
		map: (
			<g stroke={c} strokeWidth="1.6" fill="none" strokeLinejoin="round">
				<path d="m3 6 6-3 6 3 6-3v15l-6 3-6-3-6 3z" />
				<path d="M9 3v15M15 6v15" />
			</g>
		),
		pin: (
			<g stroke={c} strokeWidth="1.6" fill="none" strokeLinejoin="round">
				<path d="M12 3c-4 0-7 3-7 7 0 5 7 11 7 11s7-6 7-11c0-4-3-7-7-7Z" />
				<circle cx="12" cy="10" r="2.5" />
			</g>
		),
		key: (
			<g stroke={c} strokeWidth="1.6" fill="none" strokeLinejoin="round">
				<circle cx="7" cy="14" r="4" />
				<path d="M11 14h10v3M16 14v3" strokeLinecap="round" />
			</g>
		),
		user: (
			<path
				d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7 9c0-3.5 3.1-6 7-6s7 2.5 7 6"
				stroke={c}
				strokeWidth="1.6"
				fill="none"
				strokeLinecap="round"
			/>
		),
		arrow: (
			<path
				d="M5 12h14m-5-5 5 5-5 5"
				stroke={c}
				strokeWidth="1.6"
				fill="none"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		),
		arrowUR: (
			<path
				d="M7 17 17 7M9 7h8v8"
				stroke={c}
				strokeWidth="1.6"
				fill="none"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		),
		trend: (
			<path
				d="M3 17l6-6 4 4 8-8"
				stroke={c}
				strokeWidth="1.8"
				fill="none"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		),
		close: (
			<path
				d="M6 6l12 12M18 6 6 18"
				stroke={c}
				strokeWidth="1.8"
				strokeLinecap="round"
			/>
		),
		mail: (
			<g stroke={c} strokeWidth="1.6" fill="none" strokeLinejoin="round">
				<rect x="3" y="5" width="18" height="14" rx="2" />
				<path d="M3 7l9 7 9-7" strokeLinecap="round" />
			</g>
		),
		lock: (
			<g stroke={c} strokeWidth="1.6" fill="none" strokeLinejoin="round">
				<rect x="5" y="11" width="14" height="10" rx="2" />
				<path d="M8 11V7a4 4 0 1 1 8 0v4" />
			</g>
		),
		eyeOff: (
			<g stroke={c} strokeWidth="1.6" fill="none" strokeLinecap="round">
				<path d="M3 3l18 18" />
				<path d="M10.5 5.4A11 11 0 0 1 22 12s-1.5 2.5-4 4.5M14 14a3 3 0 1 1-4-4M6 6.5C3.5 8.5 2 12 2 12s4 7 10 7c1.5 0 2.9-.4 4-.9" />
			</g>
		),
		phone: (
			<path
				d="M5 5.5A2.5 2.5 0 0 1 7.5 3h1.8a1 1 0 0 1 1 .8l.8 4a1 1 0 0 1-.3 1L9 10.4a12 12 0 0 0 4.6 4.6l1.6-1.8a1 1 0 0 1 1-.3l4 .8a1 1 0 0 1 .8 1V17a2.5 2.5 0 0 1-2.5 2.5C12 19.5 5 12.5 5 5.5Z"
				stroke={c}
				strokeWidth="1.6"
				fill="none"
				strokeLinejoin="round"
			/>
		),
		qr: (
			<g stroke={c} strokeWidth="1.6" fill="none">
				<rect x="3" y="3" width="7" height="7" />
				<rect x="14" y="3" width="7" height="7" />
				<rect x="3" y="14" width="7" height="7" />
				<path d="M14 14h3v3h-3zM21 14v3M14 21h3M21 21h-3" />
			</g>
		),
		google: (
			<g>
				<path
					fill="#FFC107"
					d="M21.8 10.2H12v3.6h5.6c-.5 2.5-2.6 4.3-5.6 4.3-3.4 0-6.1-2.7-6.1-6.1S8.6 5.9 12 5.9c1.5 0 2.9.5 4 1.5l2.6-2.6C16.8 3.1 14.5 2.1 12 2.1 6.5 2.1 2.1 6.5 2.1 12s4.4 9.9 9.9 9.9c5.7 0 9.5-4 9.5-9.6 0-.7-.1-1.4-.2-2.1z"
				/>
			</g>
		),
		trash: (
			<g
				stroke={c}
				strokeWidth="1.6"
				fill="none"
				strokeLinecap="round"
				strokeLinejoin="round"
			>
				<path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6" />
			</g>
		),
		gear: (
			<g
				stroke={c}
				strokeWidth="1.5"
				fill="none"
				strokeLinecap="round"
				strokeLinejoin="round"
			>
				<circle cx="12" cy="12" r="3" />
				<path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
			</g>
		),
		logout: (
			<g
				stroke={c}
				strokeWidth="1.6"
				fill="none"
				strokeLinecap="round"
				strokeLinejoin="round"
			>
				<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
			</g>
		),
	};
	return (
		<svg width={s} height={s} viewBox="0 0 24 24" aria-hidden="true">
			<title>{n}</title>
			{paths[n]}
		</svg>
	);
}
